import { Type } from 'lucide-react';
import type { ReactNode } from 'react';
import { AddTile, Card, EmptyState, MicroLabel, SectionHead } from './ui/primitives';
import type { BrandFontFile, BrandTypeface } from './types';

/** The line every type specimen is set in. Long enough to show the shape of a
 * face, short enough that four cards sit side by side. */
const SPECIMEN = 'Aa';

/** The full-alphabet line under the family name, set in the family itself. */
const ALPHABET = 'ABCDEFGHIJKLM abcdefghijklm 0123456789';

/**
 * Typography: the named faces, then the files that back them.
 *
 * ── Why the specimen is set in the family ──
 *
 * `font-family: "Canela", ...` with no fallback chain would silently render the
 * app's own face and claim it is the brand's. The specimen therefore names the
 * family with a generic fallback and says, in the card, whether font FILES
 * exist — because "we have the name" and "we have the font" are different
 * states, and only one of them means a designer can actually set type.
 *
 * A face named in `typography` with no matching file is the common case today,
 * and it is worth showing as exactly that rather than hiding it.
 */
export function BrandTypography({
	typefaces,
	fontFiles,
	guidelines,
	onAdd,
	action,
}: {
	typefaces: BrandTypeface[];
	fontFiles: BrandFontFile[];
	/** `typography.guidelines` — how the scale is meant to be used. */
	guidelines?: string | null;
	/**
	 * Add a font file.
	 *
	 * The section had no way to add anything, which made it the one part of the
	 * kit you could only read. A FILE is the thing that can be added here — a
	 * typeface card is derived, either from `typography.primaryFont` or from the
	 * families of the files themselves, so uploading a font is also how a face
	 * appears when none is named.
	 */
	onAdd?: () => void;
	action?: ReactNode;
}) {
	if (typefaces.length === 0 && fontFiles.length === 0) {
		return (
			<>
				<SectionHead title="Typography" action={action} />
				{/* No action here: `SectionHead` above already carries "Add font",
				    and repeating it inside the panel gives the same control
				    twice on one screen. Colors and Logo leave theirs bare for
				    the same reason. */}
				<EmptyState
					icon={Type}
					title="No typefaces set"
					hint="Name the brand's primary and secondary faces, and upload the font files that go with them."
				/>
			</>
		);
	}

	return (
		<>
			<SectionHead
				title="Typography"
				meta={
					typefaces.length > 0
						? `${typefaces.length} ${typefaces.length === 1 ? 'typeface' : 'typefaces'}`
						: `${fontFiles.length} font files`
				}
				action={action}
			/>

			{/* Equal columns with the add tile closing the row, the way the
			    mockup lays it out — three faces and a tile read as one set. */}
			<div className="mb-3.5 grid grid-cols-[repeat(auto-fit,minmax(13rem,1fr))] gap-3.5">
				{typefaces.map((face) => (
					<TypefaceCard key={face.role} face={face} />
				))}
				{onAdd && <AddTile label="Add font" onClick={onAdd} className="min-h-[13rem]" />}
			</div>

			{guidelines && (
				<Card padded className="mb-3.5">
					<MicroLabel className="mb-2 block">How type is used</MicroLabel>
					<p className="max-w-[40rem] whitespace-pre-line text-sm leading-relaxed text-text-2">
						{guidelines}
					</p>
				</Card>
			)}

			{fontFiles.length > 0 && (
				<Card>
					{fontFiles.map((file, i) => (
						<div
							key={file.id}
							className={`flex items-center gap-3.5 px-4 py-3 ${
								i > 0 ? 'border-t border-line-1' : ''
							}`}
						>
							<div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-[var(--r-2)] border border-line-1 bg-surface-fill text-text-4">
								<Type size={14} />
							</div>
							<div className="min-w-0 flex-1">
								<div className="truncate text-sm font-medium text-text-1">
									{file.family}
								</div>
								<div className="text-xs text-text-4">
									{[file.style, file.weight].filter(Boolean).join(' · ') ||
										file.name}
								</div>
							</div>
						</div>
					))}
				</Card>
			)}
		</>
	);
}

function TypefaceCard({ face }: { face: BrandTypeface }) {
	/* Quoted so a family with spaces resolves, and with a real fallback so the
	   card never claims the app's own face is the brand's. */
	const stack = `"${face.family}", var(--font-sans, sans-serif)`;

	return (
		<Card padded className="flex flex-col gap-2.5">
			<div className="flex items-baseline justify-between gap-2">
				<MicroLabel>{face.role}</MicroLabel>
				{face.styles && face.styles.length > 0 && (
					<span className="shrink-0 truncate font-mono text-2xs text-text-4">
						{face.styles.join(' · ')}
					</span>
				)}
			</div>

			<div
				className="truncate text-5xl leading-none -tracking-[0.02em] text-text-1"
				style={{ fontFamily: stack }}
			>
				{SPECIMEN}
			</div>

			<div>
				<div className="mb-1 truncate text-sm font-medium text-text-1">{face.family}</div>
				{/* The alphabet line the mockup sets in the family itself — a
				    two-letter specimen shows a face's shape, a full one shows
				    whether the file is actually there to render it. */}
				<div className="truncate text-xs text-text-3" style={{ fontFamily: stack }}>
					{ALPHABET}
				</div>
			</div>

			{/* The mockup's note row. We have no per-face note, so it carries the
			    thing that actually matters here: whether a designer can set type
			    in this face, or only knows its name. */}
			<div className="mt-auto border-t border-line-1 pt-2 text-2xs">
				{face.hasFiles ? (
					<span className="text-text-4">Font files uploaded</span>
				) : (
					<span className="text-status-warn">Name only — no font files</span>
				)}
			</div>
		</Card>
	);
}
